import React from "react";
import { Box, CircularProgress, SxProps, Theme } from "@mui/material";
import { useFulcrumPhoto } from "../../hooks/useFulcrumPhoto";
import { isFulcrumPhotoUrl, FulcrumRegion } from "../../utilities/fulcrumLibService";
import { isEnvCdnUrl } from "../../utilities/cdnUtils";

export interface FulcrumImageProps {
  src: string | null | undefined;
  alt: string;
  region?: FulcrumRegion;
  thumbnail?: boolean;
  sx?: SxProps<Theme>;
  onError?: () => void;
  onLoad?: () => void;
}

/**
 * Image component that automatically handles Fulcrum photo authentication
 * For non-Fulcrum URLs, it behaves like a regular img tag
 */
export const FulcrumImage: React.FC<FulcrumImageProps> = ({ src, alt, region = "AU", thumbnail = true, sx, onError, onLoad }) => {
  // Clean implementation - src should now be proper URLs from data-source adapters

  const { blobUrl, loading, error } = useFulcrumPhoto(src, region, thumbnail);

  const finalImageUrl = blobUrl || src

  // Show loading state for Fulcrum photos
  if (loading && src && isFulcrumPhotoUrl(src)) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 100,
          ...sx
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state or fallback
  if (error) {
    console.warn(`[FulcrumImage] Photo loading failed: ${error}`);
    if (onError) {
      onError();
    }
  }

  return (
    <Box
      component='img'
      src={finalImageUrl || "/icons/image-placeholder.svg"}
      alt={alt}
      sx={{
        objectFit: "cover",
        ...sx
      }}
      loading='eager'
      onLoad={onLoad}
      onError={() => {
        // Enhanced error logging for better debugging
        if (src && isFulcrumPhotoUrl(src)) {
          console.warn(`[FulcrumImage] Fulcrum photo failed to load: ${src}. Error: ${error || "Image load error"}`);
        } else if (src && !src.includes("thumbnail.jpg") && !isEnvCdnUrl(src)) {
          console.warn("[FulcrumImage] Non-Fulcrum image failed to load:", src);
        }
        onError?.();
      }}
    />
  );
};
